import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Space } from 'antd';
import { Link, useHref, useNavigate } from 'react-router-dom';
import { CaretRight, Eye } from '@phosphor-icons/react';
import ServiceActions from './ServiceActions';
import ServiceState from './ServiceState';
import Disclosure from './Disclosure';
import { datastoreNote, formatSeconds, useProcesses } from './TaskManager';
import { ListRowsSkeleton } from '../Skeletons';
import useRememberedToggle from '../../hooks/useRememberedToggle';
import useRepoFlags from '../../hooks/useRepoFlags';
import RepoFlagBadges from '../repoFlags/RepoFlagBadges';
import { codeSelectionUrl } from '../../utils/codeRoute';
import { isServiceSegment } from '../../utils/openProject';
import {
  formatBytes, formatCores, formatUptime, isRunning, serviceState, stopService,
} from '../../services/SandboxService';
import { sortTableRows } from '../table/DataTable';
import './sandbox.css';
import '../../pages/SandboxHome.css';

/**
 * Home's "Running now" (docs/SANDBOX_FIRST.md): one row per project with services that run (or just crashed / failed
 * to build), with their CPU and memory summed, the longest uptime, Open and Stop; collapsed by default, it expands to
 * one row per service with its own CPU, memory and uptime, Open, Stop, Restart and Logs (the projects left open are
 * remembered for this viewer). Then, in a per-person sandbox, a disclosure with every other process
 * the task manager sees (useProcesses): the shared databases, the workspace, the preview runner and what the previews
 * use in all.
 */

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
const cores = (value) => (value === null || value === undefined ? '—' : formatCores(value));

/** "2 services · 1 project": what runs now. */
export function runningCount(services = []) {
  const running = services.filter(isRunning);
  const projects = new Set(running.map((service) => service.projectId)).size;
  return running.length ? `${plural(running.length, 'service')} · ${plural(projects, 'project')}` : null;
}

/**
 * Where a running service opens in Flow: its Code view (`tab: 'code'`) or its Preview (`tab: 'preview'`), the same
 * deep link as anywhere else (utils/codeRoute.js). Null without a tenant or a real service name (the project root `.`).
 */
/** The project's Apps tab on this service (`?tab=apps&service=…`), or null. */
export function serviceAppsUrl(tenantId, service) {
  if (!tenantId || !service?.projectId || !isServiceSegment(service.serviceName)) { return null; }
  const search = `?tab=apps&service=${encodeURIComponent(service.serviceName)}`;
  return codeSelectionUrl({ tenantId, projectId: service.projectId, search });
}

export function serviceTabUrl(tenantId, service, tab) {
  if (!tenantId || !service?.projectId || !isServiceSegment(service.serviceName)) { return null; }
  return codeSelectionUrl({
    tenantId, projectId: service.projectId, service: service.serviceName, branch: service.branch || 'main', tab,
  });
}

/** The live services by project, in the order they come: `[{ projectId, projectName, services }]`. */
export function groupByProject(services = []) {
  const groups = new Map();
  services.forEach((service) => {
    const key = service.projectId;
    if (!groups.has(key)) { groups.set(key, { projectId: key, projectName: service.projectName || key, services: [] }); }
    groups.get(key).services.push(service);
  });
  return [...groups.values()];
}

/** A service's CPU and memory: the overview's own reading, else the task manager's (`measured`, by session id). */
export function serviceUsage(service, measured = new Map()) {
  const seen = measured.get(service.sessionId);
  return {
    cpu: service.resources ? service.resources.cpuPercent : seen?.cpuPercent,
    memory: service.resources ? service.resources.memoryBytes : seen?.memoryBytes,
  };
}

const sum = (values) => {
  const known = values.filter((value) => value !== null && value !== undefined && !Number.isNaN(Number(value)));
  return known.length ? known.reduce((total, value) => total + Number(value), 0) : null;
};

/**
 * A project's row: its services' CPU and memory summed (null when none is measured) and since when the oldest running
 * one is up (its longest uptime), null when none runs.
 */
export function projectTotals(services = [], measured = new Map()) {
  const usage = services.map((service) => serviceUsage(service, measured));
  const since = services.filter(isRunning)
    .map((service) => service.startedAt || service.createdAt)
    .filter((value) => value && Number.isFinite(new Date(value).getTime()))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] || null;
  return { cpu: sum(usage.map((entry) => entry.cpu)), memory: sum(usage.map((entry) => entry.memory)), since };
}

const OPEN_KEY = 'flow.home.runningProjectsOpen';

const readOpen = () => {
  try {
    const value = JSON.parse(window.localStorage.getItem(OPEN_KEY) || '[]');
    return Array.isArray(value) ? value.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

/** The projects whose services are shown, remembered for this viewer in this browser; none by default. */
export function useOpenProjects() {
  const [openIds, setOpenIds] = useState(readOpen);
  const toggle = useCallback((projectId) => {
    setOpenIds((current) => {
      const next = current.includes(projectId) ? current.filter((id) => id !== projectId) : [...current, projectId];
      try { window.localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch { /* not remembered, still toggles */ }
      return next;
    });
  }, []);
  return [openIds, toggle];
}

/** True for a plain left click: the others (cmd/ctrl/shift/middle) are the browser's, to open a new tab or window. */
const plainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/** "Open": a real link to a Preview, styled as the row's other buttons; a plain click stays in the app. */
function OpenPreview({ to, name }) {
  const href = useHref(to);
  const navigate = useNavigate();
  return (
    <Button
      aria-label={`Open the preview of ${name}`}
      href={href}
      onClick={(event) => { if (plainClick(event)) { event.preventDefault(); navigate(to); } }}
      icon={<Eye aria-hidden size={14} />}
      size="small"
    >
      Open
    </Button>
  );
}

OpenPreview.propTypes = { to: PropTypes.string.isRequired, name: PropTypes.string.isRequired };

export function Row({ name, cpu, memory, up, running = true, children = null, className = undefined, onClick = undefined, testId = undefined, compact = false }) {
  const dot = <span aria-hidden="true" className={`sbx-run-dot${running ? '' : ' off'}`} />;
  return (
    <tr className={className} data-testid={testId} onClick={onClick}>
      {/* Compact (the project Home's Apps): the dot, the name and its state on one line, the name cut to the column. */}
      <td className="sbx-run-name">{compact ? <span className="sbx-run-namebox">{dot}{name}</span> : <>{dot}{name}</>}</td>
      <td className="sbx-run-num" data-label="CPU">{cpu}</td>
      <td className="sbx-run-num" data-label="Memory">{memory}</td>
      <td className="sbx-run-num" data-label="Up">{up}</td>
      <td className="sbx-actions-cell">{children}</td>
    </tr>
  );
}

Row.propTypes = {
  name: PropTypes.node.isRequired, cpu: PropTypes.node, memory: PropTypes.node, up: PropTypes.node, running: PropTypes.bool, children: PropTypes.node,
  className: PropTypes.string, onClick: PropTypes.func, testId: PropTypes.string, compact: PropTypes.bool,
};

/**
 * One service's row, the same in Running now (an expanded project) and on the project Home's Apps: its name linking to
 * its Code view, its state (with restarts) unless it simply runs, its CPU, memory and uptime, then Open, Start / Stop,
 * Restart and Logs (ServiceActions). `service` is the runner's entry, or null for a service that never ran (Start
 * builds it). `canRun` false (a viewer): Open and Logs only. `onOpen` / `onOpenCode`: open the service in place (the
 * project Home, already in the project); without them Open is a link to its Preview and the name a link to its Code.
 * `extra`: more controls after the actions (Home's "⋯"). `alwaysShowState`: the state even when it simply runs (Home,
 * where stopped services are listed too). A flagged service shows its "Read-only" / "Context only" badges.
 * `compact` (the project Home's Apps, a table of its own): one line per service, the name and its state in the name
 * cell, the actions in one group that never wraps: Start / Stop, Open, Logs, then `extra` (Restart is in the "⋯", asked
 * through `restartRequest`). `hideOpenLogs`: no Open and Logs buttons (a narrow table, the "⋯" has them).
 */
export function ServiceRow({
  projectId, serviceName, service = null, label = null, tenantId = null, measured = new Map(), canRun = true,
  onChanged = () => {}, onOpenLogs = null, onOpen = null, onOpenCode = null, onOpenApp = null, appUrl = null, extra = null, testId = undefined, alwaysShowState = false, showRemove = true,
  compact = false, restartRequest = 0, hideOpenLogs = false, sub = null,
}) {
  const target = { projectId, serviceName, branch: service?.branch };
  const flags = useRepoFlags(projectId).serviceFlags(serviceName);
  const name = label || serviceName;
  const { cpu, memory } = serviceUsage(service || {}, measured);
  const running = isRunning(service);
  const healthy = !alwaysShowState && serviceState(service) === 'running' && !service?.restartCount;
  const codeUrl = serviceTabUrl(tenantId, target, 'code');
  const previewUrl = serviceTabUrl(tenantId, target, 'preview');
  const open = hideOpenLogs ? null : onOpen
    ? <Button aria-label={`Open ${name} in Flow`} icon={<Eye aria-hidden size={14} />} onClick={onOpen} size="small">Open</Button>
    : previewUrl ? <OpenPreview name={name} to={previewUrl} /> : null;
  const logs = hideOpenLogs ? null : onOpenLogs;
  const actions = canRun ? (
    <ServiceActions
      before={compact ? null : open}
      beforeLogs={compact ? open : null}
      onChanged={onChanged}
      onOpenLogs={logs || undefined}
      projectId={projectId}
      restartRequest={restartRequest}
      service={service}
      serviceName={serviceName}
      showRemove={showRemove}
      showRestart={!compact}
      wrap={!compact}
    />
  ) : null;
  const openCode = (event) => { if (onOpenCode && plainClick(event)) { event.preventDefault(); onOpenCode(); } };
  const viewerActions = (
    <>
      {open}
      {logs && <Button onClick={() => logs(serviceName)} size="small">Logs</Button>}
    </>
  );
  const nameClass = compact ? 'sbx-link sbx-mono sbx-run-code sbx-run-label' : 'sbx-link sbx-mono sbx-run-code';
  return (
    <Row
      className="sbx-run-child"
      compact={compact}
      cpu={cores(cpu)}
      memory={formatBytes(memory)}
      name={(
        <span className="sbx-run-service">
          {appUrl ? (
            <Link className={nameClass} title={`Open ${name} in its project's Apps`} to={appUrl}>{name}</Link>
          ) : onOpenApp ? (
            <button className={nameClass} onClick={onOpenApp} title={`Open ${name} in Apps`} type="button">{name}</button>
          ) : codeUrl ? (
            <Link className={nameClass} onClick={openCode} title={`Open the code of ${serviceName}`} to={codeUrl}>
              {name}
            </Link>
          ) : onOpenCode ? (
            <button className={nameClass} onClick={onOpenCode} title={`Open the code of ${serviceName}`} type="button">{name}</button>
          ) : <span className={compact ? 'sbx-mono sbx-run-label' : 'sbx-mono'} title={name}>{name}</span>}
          {healthy ? null : <> <ServiceState service={service} /></>}
          <RepoFlagBadges flags={flags} testId={`service-flags-${serviceName}`} />
          {sub}
        </span>
      )}
      running={running}
      testId={testId}
      up={running ? formatUptime(service.startedAt || service.createdAt) : '—'}
    >
      {compact ? (
        <span className="sbx-run-actions" data-testid="service-row-actions">
          {canRun ? actions : viewerActions}
          {extra}
        </span>
      ) : canRun && !extra ? actions : (
        <Space size={6} wrap>
          {canRun ? actions : viewerActions}
          {extra}
        </Space>
      )}
    </Row>
  );
}

ServiceRow.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  service: PropTypes.object,
  label: PropTypes.string,
  tenantId: PropTypes.string,
  measured: PropTypes.instanceOf(Map),
  canRun: PropTypes.bool,
  onChanged: PropTypes.func,
  onOpenLogs: PropTypes.func,
  onOpen: PropTypes.func,
  appUrl: PropTypes.string,
  onOpenApp: PropTypes.func,
  onOpenCode: PropTypes.func,
  extra: PropTypes.node,
  testId: PropTypes.string,
  alwaysShowState: PropTypes.bool,
  showRemove: PropTypes.bool,
  compact: PropTypes.bool,
  restartRequest: PropTypes.number,
  hideOpenLogs: PropTypes.bool,
  // Under the name: a compose app's services as small tags (Home's Apps).
  sub: PropTypes.node,
};

/** A project's totals: its services' CPU and memory summed, the longest uptime (projectTotals). */
export function TotalsRow({
  name, services = [], measured = new Map(), children = null, className = 'sbx-run-project', testId = undefined, onClick = undefined, compact = false,
}) {
  const totals = projectTotals(services, measured);
  return (
    <Row
      className={className}
      compact={compact}
      onClick={onClick}
      cpu={cores(totals.cpu)}
      memory={formatBytes(totals.memory)}
      name={name}
      running={services.some(isRunning)}
      testId={testId}
      up={totals.since ? formatUptime(totals.since) : '—'}
    >
      {children}
    </Row>
  );
}

TotalsRow.propTypes = {
  name: PropTypes.node.isRequired, services: PropTypes.array, measured: PropTypes.instanceOf(Map), children: PropTypes.node,
  className: PropTypes.string, testId: PropTypes.string, onClick: PropTypes.func, compact: PropTypes.bool,
};

/** A click on the row itself (not on one of its links or buttons) toggles it, as the caret does. */
const onRowItself = (handler) => (event) => {
  if (event.target instanceof Element && event.target.closest('a, button, input, [role="button"]')) { return; }
  handler();
};

/** One project: its row (caret, name, "N services", totals, Open and Stop) and, when expanded, its services' rows. */
function ProjectGroup({
  group, expanded, onToggle, measured, tenantId, onOpenProject, onOpenLogs, onChanged, sort = null,
}) {
  const [stopping, setStopping] = useState(false);
  const { projectId, projectName, services } = group;
  const running = services.filter(isRunning);
  const previewService = running.find((service) => serviceTabUrl(tenantId, service, 'preview')) || services.find((service) => serviceTabUrl(tenantId, service, 'preview'));
  const previewUrl = previewService ? serviceTabUrl(tenantId, previewService, 'preview') : null;
  const regionId = `sbx-run-project-${String(projectId).replace(/[^A-Za-z0-9_-]/g, '_')}`;
  const stopProject = async() => {
    setStopping(true);
    await Promise.allSettled(running.map((service) => stopService(service.sessionId)));
    setStopping(false);
    onChanged();
  };

  return (
    <>
      <tbody className="sbx-run-group">
        <TotalsRow
          className={`sbx-run-project${expanded ? ' open' : ''}`}
          measured={measured}
          services={services}
          name={(
            <span className="sbx-run-service">
              <button
                aria-controls={regionId}
                aria-expanded={expanded}
                aria-label={`${projectName} services`}
                className="sbx-run-caret"
                onClick={onToggle}
                type="button"
              >
                <CaretRight aria-hidden="true" className={`sbx-disclosure-caret${expanded ? ' open' : ''}`} size={14} />
              </button>
              <button className="sbx-link" onClick={() => onOpenProject(projectId)} type="button">{projectName}</button>
              <span className="sbx-run-note"> · {plural(services.length, 'service')}</span>
            </span>
          )}
          onClick={onRowItself(onToggle)}
        >
          <Space size={6} wrap>
            {previewUrl && <OpenPreview name={projectName} to={previewUrl} />}
            {running.length > 0 && (
              <Button aria-label={`Stop ${projectName}`} loading={stopping} onClick={stopProject} size="small">Stop</Button>
            )}
          </Space>
        </TotalsRow>
      </tbody>
      <tbody className="sbx-run-children" hidden={!expanded} id={regionId}>
        {expanded && sortRunItems(services, sort, (service, key) => serviceSortValue(service, key, measured)).map((service) => (
          <ServiceRow
            appUrl={serviceAppsUrl(tenantId, service)}
            key={service.sessionId}
            measured={measured}
            onChanged={onChanged}
            onOpenLogs={(name) => onOpenLogs(service.projectId, name)}
            projectId={service.projectId}
            service={service}
            serviceName={service.serviceName}
            tenantId={tenantId}
          />
        ))}
      </tbody>
    </>
  );
}

ProjectGroup.propTypes = {
  group: PropTypes.shape({ projectId: PropTypes.string, projectName: PropTypes.string, services: PropTypes.array }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  measured: PropTypes.instanceOf(Map).isRequired,
  tenantId: PropTypes.string,
  onOpenProject: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  sort: PropTypes.object,
};

const HEAD_COLUMNS = [
  { key: 'name', label: null },
  { key: 'cpu', label: 'CPU' },
  { key: 'memory', label: 'Memory' },
  { key: 'up', label: 'Up' },
];

/**
 * The header of the task-manager tables (Running now, Processes, a project's Apps). With `onSort` every column sorts
 * (`sort`: `{ key, dir }`, from useRunSort): its header is a button, `aria-sort` says which way.
 */
export const Head = ({ first, sort = null, onSort = null }) => (
  <thead>
    <tr>
      {HEAD_COLUMNS.map((column) => {
        const label = column.label || first;
        if (!onSort) { return <th key={column.key} scope="col">{label}</th>; }
        const on = sort?.key === column.key;
        return (
          <th aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} key={column.key} scope="col">
            <button className="dt-sort" onClick={() => onSort(column.key)} type="button">
              <span className="dt-sort-label">{label}</span>
              <span aria-hidden="true" className={`dt-sort-arrow${on ? ' is-on' : ''}`}>{on ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}</span>
            </button>
          </th>
        );
      })}
      <th aria-label="Actions" scope="col" />
    </tr>
  </thead>
);
Head.propTypes = { first: PropTypes.string.isRequired, onSort: PropTypes.func, sort: PropTypes.object };

/** The sort of a task-manager table: `[sort, sortBy]`; a second click on a column turns its order around. */
export function useRunSort() {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const sortBy = useCallback((key) => setSort((now) => (now.key === key ? { key, dir: now.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);
  return [sort, sortBy];
}

const sinceMs = (value) => (value && Number.isFinite(new Date(value).getTime()) ? new Date(value).getTime() : null);

/** What a service's row sorts on: name, CPU, memory, and how long it is up (the earlier it started, the longer). */
export function serviceSortValue(service, key, measured = new Map(), name = null) {
  if (key === 'name') { return name || service?.serviceName || ''; }
  const usage = serviceUsage(service || {}, measured);
  if (key === 'cpu') { return usage.cpu; }
  if (key === 'memory') { return usage.memory; }
  if (key === 'up') { const since = isRunning(service) ? sinceMs(service.startedAt || service.createdAt) : null; return since === null ? null : -since; }
  return null;
}

/** `items` sorted on `value(item, key)` by `sort` (blanks last, as every Flow table); unsorted without a key. */
export function sortRunItems(items, sort, value) {
  if (!sort?.key) { return items; }
  return sortTableRows(items, { value: (item) => value(item, sort.key) }, sort.dir);
}

/** The Processes table's rows: each datastore, the workspace, the preview runner, with what each sorts on. */
function processRows(data, workspace, runner) {
  const rows = (data.datastores || []).map((store) => {
    const note = datastoreNote(store);
    const up = store.running && store.uptimeSecs !== null ? store.uptimeSecs : null;
    return {
      key: store.key,
      cpu: cores(store.cpuPercent),
      memory: formatBytes(store.memoryBytes),
      name: <span>{store.store}{note ? <span className="sbx-run-note"> · {note}</span> : null}</span>,
      running: Boolean(store.running),
      up: up === null ? '—' : formatSeconds(up),
      sort: { name: store.store, cpu: store.cpuPercent, memory: store.memoryBytes, up },
    };
  });
  if (workspace) {
    rows.push({
      key: 'workspace', cpu: cores(workspace.cpuPercent), memory: formatBytes(workspace.memoryBytes), name: 'Workspace (files, Git, AI tools)', running: true, up: '—',
      sort: { name: 'Workspace (files, Git, AI tools)', cpu: workspace.cpuPercent, memory: workspace.memoryBytes, up: null },
    });
  }
  if (runner && runner.memoryBytes !== null && runner.memoryBytes !== undefined) {
    rows.push({
      key: 'runner', cpu: cores(runner.cpuPercent), memory: formatBytes(runner.memoryBytes), name: 'Preview runner', running: true, up: formatSeconds(runner.uptimeSecs),
      sort: { name: 'Preview runner', cpu: runner.cpuPercent, memory: runner.memoryBytes, up: runner.uptimeSecs },
    });
  }
  return rows;
}

/** The rest of what the task manager sees: databases, the workspace, the runner, the previews' total. */
function Processes({ sandbox, awake, data, error, loaded }) {
  const [sort, sortBy] = useRunSort();
  if (!awake) { return <p className="sbx-run-empty">Your sandbox is asleep: nothing runs. It wakes on the next request.</p>; }
  if (error) { return <p className="sbx-run-empty" role="alert">The process list could not be read ({error.message}).</p>; }
  if (!data && loaded) {
    return <p className="sbx-run-empty">Your sandbox runs an older version without this list. It updates the next time it wakes: put it to sleep, then open a project.</p>;
  }
  if (!data) { return <p className="sbx-run-empty">Loading what runs…</p>; }
  if (!data.available) { return <p className="sbx-run-empty">The full process list needs a newer preview runner in your sandbox.</p>; }
  const workspace = sandbox?.usage?.workspace;
  const previews = sandbox?.usage?.previews;
  const runner = data.runner;
  return (
    <>
      <h3 className="sbx-label sbx-run-sub">Databases and the sandbox itself</h3>
      <div className="sbx-table-wrap">
        <table aria-label="Databases and the sandbox itself" className="sbx-table sbx-run-table">
          <Head first="Process" onSort={sortBy} sort={sort} />
          <tbody>
            {sortRunItems(processRows(data, workspace, runner), sort, (row, key) => row.sort[key]).map((row) => (
              <Row cpu={row.cpu} key={row.key} memory={row.memory} name={row.name} running={row.running} up={row.up} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="sbx-run-foot">
        {previews ? `Previews use ${cores(previews.cpuPercent)} and ${formatBytes(previews.memoryBytes)} in all. ` : ''}
        Databases start and stop with the projects that use them.
        {data.supported === false ? ' CPU and memory are measured only where the runner can read its processes.' : ''}
      </p>
    </>
  );
}

Processes.propTypes = { sandbox: PropTypes.object, awake: PropTypes.bool, data: PropTypes.object, error: PropTypes.object, loaded: PropTypes.bool };

export default function RunningNow({
  tenantId = null, sandbox = null, loading = false, onOpenProject, onOpenLogs, onChanged = () => {}, onStopAll, stoppingAll = false, intervalMs = 5000, sleepAction = null,
}) {
  const perPerson = sandbox?.mode === 'per-person';
  const awake = sandbox?.state === 'running';
  // The task manager's data, only for a per-person sandbox that is awake (TaskManager.jsx).
  const { data, error, loaded, refresh } = useProcesses({ enabled: perPerson && awake, intervalMs });
  const [open, toggle] = useRememberedToggle('flow.home.processesOpen');
  const [openProjects, toggleProject] = useOpenProjects();
  const services = sandbox?.services || [];
  const waiting = loading && !sandbox;
  const live = services.filter((service) => isRunning(service) || service.crashed || service.buildStatus === 'failed');
  const count = runningCount(live);
  const measured = new Map((data?.services || []).map((service) => [service.sessionId, service]));
  const changed = () => { if (perPerson && awake) { refresh(); } onChanged(); };
  const [sort, sortBy] = useRunSort();
  // A project sorts on its totals (its name, its services' CPU and memory summed, its longest uptime); its services too.
  const groups = sortRunItems(groupByProject(live), sort, (group, key) => {
    if (key === 'name') { return group.projectName; }
    const totals = projectTotals(group.services, measured);
    if (key === 'up') { return sinceMs(totals.since) === null ? null : -sinceMs(totals.since); }
    return totals[key];
  });

  return (
    <section aria-labelledby="sbx-running" className="sbx-card sbx-home-section sbx-run">
      <div className="sbx-run-head">
        <h2 id="sbx-running">Running now</h2>
        {count && <span className="sbx-pill">{count}</span>}
        <span className="sbx-run-spacer" />
        {live.some(isRunning) && <Button loading={stoppingAll} onClick={() => onStopAll(live.filter(isRunning))} size="small">Stop all</Button>}
      </div>
      {waiting && <ListRowsSkeleton label="Loading what runs now" rows={2} />}
      {sandbox?.servicesUnavailable && <p className="sbx-run-empty">The service list needs a newer preview runner.</p>}
      {!waiting && !sandbox?.servicesUnavailable && live.length === 0 && (
        <p className="sbx-run-empty">Nothing is running.</p>
      )}
      {live.length > 0 && (
        <div className="sbx-table-wrap">
          <table aria-label="Services running now" className="sbx-table sbx-run-table">
            <Head first="Project" onSort={sortBy} sort={sort} />
            {groups.map((group) => (
              <ProjectGroup
                expanded={openProjects.includes(group.projectId)}
                group={group}
                key={group.projectId}
                measured={measured}
                onChanged={changed}
                onOpenLogs={onOpenLogs}
                onOpenProject={onOpenProject}
                onToggle={() => toggleProject(group.projectId)}
                sort={sort}
                tenantId={tenantId}
              />
            ))}
          </table>
        </div>
      )}
      {perPerson && (
        <>
          <div className="sbx-disclosure-row">
            <Disclosure controls="sbx-processes" onToggle={toggle} open={open}>
              {open ? 'Hide processes' : 'All processes: databases and workspace'}
            </Disclosure>
          </div>
          <div className="sbx-run-more" hidden={!open} id="sbx-processes">
            {open && <Processes awake={awake} data={data} error={error} loaded={loaded} sandbox={sandbox} />}
            {open && sleepAction && (
              <div className="sbx-run-sleep">
                <p className="sbx-run-foot">Put the whole sandbox to sleep. It wakes up when you open Projects, a project or Chat.</p>
                {sleepAction}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

RunningNow.propTypes = {
  /** The tenant the services' Code and Preview links open in; without it the names stay plain text. */
  tenantId: PropTypes.string,
  sandbox: PropTypes.object,
  loading: PropTypes.bool,
  onOpenProject: PropTypes.func.isRequired,
  onOpenLogs: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
  onStopAll: PropTypes.func.isRequired,
  stoppingAll: PropTypes.bool,
  intervalMs: PropTypes.number,
  /** The Put to sleep button (Home owns the call): shown under All processes, not among the main actions. */
  sleepAction: PropTypes.node,
};
